import { FC, useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useTranslation } from "react-i18next"

import { Tabs } from "components/navigation/Tabs"
import { TopBar } from "components/navigation/TopBar"
import { TOP_TABS } from "components/risk/constants"
import { DashboardContent } from "components/risk/dashboard/DashboardContent"
import { ReviewPanel } from "components/risk/review/ReviewPanel"
import { RiskSettingsPanel } from "components/risk/settings/RiskSettingsPanel"

import { riskService } from "services/risk/risk.service"
import { RiskDashboardData } from "services/risk/types"

type TopTab = "dashboard" | "review" | "settings"

export const RiskDashboard: FC = () => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const navigate = useNavigate()
  const [topTab, setTopTab] = useState<TopTab>("dashboard")
  const [data, setData] = useState<RiskDashboardData | null>(null)
  const [loading, setLoading] = useState(true)

  const loadDashboard = useCallback(async () => {
    setLoading(true)
    const result = await riskService.getDashboard()
    if (result) setData(result)
    setLoading(false)
  }, [])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  const renderContent = () => {
    switch (topTab) {
      case "dashboard":
        return <DashboardContent data={data} loading={loading} navigate={navigate} />
      case "review":
        return <ReviewPanel />
      case "settings":
        return <RiskSettingsPanel />
    }
  }

  const tabs = TOP_TABS.map((tab) => ({ ...tab, label: t(`risk.tabs.${tab.key}`) }))

  return (
    <div className="relative w-full">
      <div className="flex flex-col items-start w-full gap-6 p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={t("risk.title")} />
        <Tabs tabs={tabs} activeTab={topTab} onChange={(key) => setTopTab(key as TopTab)} />

        {renderContent()}
      </div>
    </div>
  )
}
